import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  type DragEndEvent,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  FormLabel,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  Link,
  Stack,
} from "@mui/material";
import {
  Alert,
  Button,
  paletteVar,
  type DialogComponentProps,
  Typography,
  TextField,
} from "@platform/design-system";
import { validateSharePointFileName } from "@platform/fe-utils";
import { format, isValid, parseISO } from "date-fns";
import {
  type ChangeEvent,
  type DragEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { read, type WorkBook } from "xlsx";

import useExcelAppendFile from "../../../../hooks/useExcelAppendFile";
import useGenerateMarketingPresentation from "../../../../hooks/useGenerateMarketingPresentation";
import { useRfpClientDetails } from "../../../../hooks/useRfpClientDetails";
import { useRfpDetails } from "../../../../hooks/useRfpDetails";
import {
  useRfpConfiguration,
  type RfpConfiguration,
} from "../../../../hooks/useRfpConfiguration";
import useToastNotificationService from "../../../../hooks/useToastNotificationService";
import {
  arrayBufferToBase64,
  base64ToArrayBuffer,
  downloadBinaryFile,
} from "../../../../utils";

import { loadAllPagesOrder } from "./CarrierSequenceContext";

const formLabelSx = { color: "text.primary", fontWeight: 500 } as const;

// configuration.configuration is the settings payload: the outer object is the
// configuration record, the inner one its field settings and template.
const readConfiguration = (
  configuration: RfpConfiguration["configuration"],
): {
  selectedTemplate: string | null;
} => ({
  selectedTemplate: configuration?.configuration?.template ?? null,
});

// Characters that are illegal in file names on Windows/most browsers, mirroring
// validateFileName in ../../../../utils plus the backslash path separator.
const invalidFileNameCharacters = /["*/?<>:|\\]/g;

const buildDefaultExportFileName = (
  clientName: string | undefined,
  effectiveDate: string | null | undefined,
): string => {
  const sanitizedName = (clientName ?? "")
    .trim()
    .replace(invalidFileNameCharacters, "")
    .trim()
    .replace(/\s+/g, " ");
  if (!sanitizedName) {
    return "";
  }

  // Use the date-only portion so a time component (e.g. a trailing "Z") cannot
  // shift the calendar day when parsed/formatted in a non-UTC timezone.
  const dateOnly = effectiveDate?.split("T")[0];
  const parsedDate = dateOnly ? parseISO(dateOnly) : null;
  const formattedDate =
    parsedDate && isValid(parsedDate) ? format(parsedDate, "MM-dd-yyyy") : null;

  return formattedDate
    ? `${sanitizedName} - Marketing Presentation - ${formattedDate}`
    : `${sanitizedName} - Marketing Presentation`;
};

const useAutoExportFileName = (
  rfpId: string | undefined,
): { fileName: string; isLoading: boolean } => {
  const { data: clientDetails, isLoading: isClientDetailsLoading } =
    useRfpClientDetails({
      rfpId: rfpId ?? "",
      enabled: !!rfpId,
    });
  const { data: rfpDetails, isLoading: isRfpDetailsLoading } = useRfpDetails({
    rfpId: rfpId ?? "",
    enabled: !!rfpId,
  });

  const effectiveDate =
    rfpDetails?.revisedEffectiveDate ?? rfpDetails?.originalEffectiveDate;

  return {
    fileName: buildDefaultExportFileName(clientDetails?.name, effectiveDate),
    isLoading: isClientDetailsLoading || isRfpDetailsLoading,
  };
};

// Case-insensitive so a typed ".XLSX" isn't kept and re-suffixed into a
// "Name.XLSX.xlsx" double extension.
const stripXlsxExtension = (fileName: string): string =>
  fileName.replace(/\.xlsx$/i, "");

const getExportFileNameWithExtension = (fileName: string): string =>
  `${stripXlsxExtension(fileName)}.xlsx`;

const getWorkbookFile = (fileList: FileList | null): File | null =>
  fileList?.[0] ?? null;

const mockWorkbookProcessingDelayMs = 300;

type SheetOrderItem = {
  id: string;
  title: string;
  description: string;
  source?: "workbook";
};

const defaultSheetOrderItems: SheetOrderItem[] = [
  { id: "client-profile", title: "Client Profile", description: "Description" },
  {
    id: "carrier-financial-rating",
    title: "Carrier Financial Ratings",
    description: "Description",
  },
  {
    id: "carrier-response",
    title: "Carrier Response",
    description: "Description",
  },
  {
    id: "comps-subs-credits",
    title: "Comp, Subs, Credits",
    description: "Description",
  },
  {
    id: "carrier-notes",
    title: "Carrier Notes",
    description: "Description",
  },
  {
    id: "financial-summary",
    title: "Financial Summary",
    description: "Description",
  },
  { id: "quotes", title: "Quotes", description: "Description" },
];

// Per-RFP key for the analyst's remembered ordering of the whole sheet list —
// fixed categories and any attached workbook's sheets interleaved — so
// reopening the drawer or exporting again restores it (CD-379).
const sheetOrderStorageKeyPrefix = "mp-sheet-order-";

const buildSheetOrderStorageKey = (rfpId: string): string =>
  `${sheetOrderStorageKeyPrefix}${rfpId}`;

const loadSavedSheetOrderIds = (rfpId: string | undefined): string[] | null => {
  if (!rfpId) return null;
  try {
    const raw = localStorage.getItem(buildSheetOrderStorageKey(rfpId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) && parsed.every((id) => typeof id === "string")
      ? (parsed as string[])
      : null;
  } catch {
    return null;
  }
};

const saveSheetOrderIds = (
  rfpId: string | undefined,
  items: SheetOrderItem[],
): void => {
  if (!rfpId) return;
  try {
    localStorage.setItem(
      buildSheetOrderStorageKey(rfpId),
      JSON.stringify(items.map((item) => item.id)),
    );
  } catch {
    // Remembering the order is best-effort — ignore any write failure
    // (quota exceeded, localStorage disabled/unavailable, security errors).
  }
};

// Reapplies a saved id order to a current set of sheets: ids the saved order
// covers keep their saved relative position (fixed categories and workbook
// sheets interleaved); any id it never saw — a newly added default category,
// or a newly uploaded/renamed workbook sheet — is appended at the end in the
// given items' own order. A saved id no longer present (a removed/renamed
// sheet) is dropped.
const applySavedSheetOrder = (
  savedIds: string[] | null,
  items: SheetOrderItem[],
): SheetOrderItem[] => {
  if (!savedIds || savedIds.length === 0) return items;
  const remaining = new Map(items.map((item) => [item.id, item]));
  const ordered: SheetOrderItem[] = [];
  for (const id of savedIds) {
    const item = remaining.get(id);
    if (item) {
      ordered.push(item);
      remaining.delete(id);
    }
  }
  for (const item of items) {
    if (remaining.has(item.id)) ordered.push(item);
  }
  return ordered;
};

// Places newly-parsed workbook sheets among items already on screen, without
// moving any already-present item — unlike applySavedSheetOrder, which
// reorders everything to match the saved order. appendWorkbookSheetsFromFile
// loads the saved order before its await, so reapplying it wholesale would
// silently discard a drag the user made on a fixed category while the parse
// was still in flight. A workbook sheet the saved order never mentioned
// (fresh upload, or renamed since the save) goes at the end, in parse order.
const insertWorkbookSheetItems = (
  savedIds: string[] | null,
  liveNonWorkbookItems: SheetOrderItem[],
  workbookSheetItems: SheetOrderItem[],
): SheetOrderItem[] => {
  if (!savedIds || savedIds.length === 0) {
    return [...liveNonWorkbookItems, ...workbookSheetItems];
  }

  const liveIds = new Set(liveNonWorkbookItems.map((item) => item.id));
  const workbookItemsById = new Map(
    workbookSheetItems.map((item) => [item.id, item]),
  );
  const itemsByAnchor = new Map<string | null, SheetOrderItem[]>();
  const seenWorkbookIds = new Set<string>();
  let anchor: string | null = null;

  for (const id of savedIds) {
    if (liveIds.has(id)) {
      anchor = id;
      continue;
    }
    const workbookItem = workbookItemsById.get(id);
    if (!workbookItem) continue;
    seenWorkbookIds.add(id);
    itemsByAnchor.set(anchor, [
      ...(itemsByAnchor.get(anchor) ?? []),
      workbookItem,
    ]);
  }

  const placed: SheetOrderItem[] = [...(itemsByAnchor.get(null) ?? [])];
  for (const item of liveNonWorkbookItems) {
    placed.push(item, ...(itemsByAnchor.get(item.id) ?? []));
  }
  placed.push(
    ...workbookSheetItems.filter((item) => !seenWorkbookIds.has(item.id)),
  );
  return placed;
};

// Per-RFP key for "the analyst removed the attached workbook." Chameleon only
// ever deletes the SharePoint append file as a side effect of Generate &
// Export (CD-1517), so removing it here alone doesn't reach the backend —
// without this, reopening the drawer re-fetches the file Chameleon still has
// and the removal appears to have been undone.
const appendFileRemovedStorageKeyPrefix = "mp-append-file-removed-";

const buildAppendFileRemovedStorageKey = (rfpId: string): string =>
  `${appendFileRemovedStorageKeyPrefix}${rfpId}`;

const loadIsAppendFileRemoved = (rfpId: string | undefined): boolean => {
  if (!rfpId) return false;
  try {
    return (
      localStorage.getItem(buildAppendFileRemovedStorageKey(rfpId)) === "true"
    );
  } catch {
    return false;
  }
};

// Cleared (not just set false) once removed is false, so a stale key never
// lingers in localStorage past the point it's relevant.
const saveIsAppendFileRemoved = (
  rfpId: string | undefined,
  removed: boolean,
): void => {
  if (!rfpId) return;
  try {
    if (removed) {
      localStorage.setItem(buildAppendFileRemovedStorageKey(rfpId), "true");
    } else {
      localStorage.removeItem(buildAppendFileRemovedStorageKey(rfpId));
    }
  } catch {
    // Remembering the removal is best-effort — ignore any write failure
    // (quota exceeded, localStorage disabled/unavailable, security errors).
  }
};

const workbookSheetIdPrefix = "workbook-sheet-";

// Title-based so the same-named sheet keeps its id across re-parses of the
// same or a re-uploaded workbook — Excel sheet names are already unique
// within one workbook, so this is collision-free.
const normalizeSheetId = (sheetName: string): string =>
  `${workbookSheetIdPrefix}${encodeURIComponent(sheetName.trim().toLowerCase())}`;

const buildWorkbookSheetOrderItems = (sheetNames: string[]): SheetOrderItem[] =>
  sheetNames.map((sheetName) => ({
    id: normalizeSheetId(sheetName),
    title: sheetName,
    description: "Manually uploaded",
    source: "workbook",
  }));

// SheetJS convention: Hidden is 0/undefined for visible, 1 for hidden, 2 for
// very hidden. Both hidden states are excluded from the drawer.
const getVisibleSheetNames = (workbook: WorkBook): string[] =>
  workbook.SheetNames.filter(
    (_sheetName, index) => !workbook.Workbook?.Sheets?.[index]?.Hidden,
  );

// Chameleon truncates an appended sheet name to Excel's 31-character limit
// and renames it on collision against the target template's own sheets — the
// drawer has no visibility into the template's sheet names, so it can only
// warn about the half of that it can know for certain: a name too long to
// survive export unchanged. A colliding-but-otherwise-valid name isn't
// detected here.
const excelSheetNameMaxLength = 31;

const findSheetNamesExceedingExcelLimit = (sheetNames: string[]): string[] =>
  sheetNames.filter((name) => name.length > excelSheetNameMaxLength);

type SortableSheetOrderItemProps = {
  item: SheetOrderItem;
  index: number;
};

const SortableSheetOrderItem = ({
  item,
  index,
}: SortableSheetOrderItemProps): React.JSX.Element => {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: item.id });

  return (
    <ListItem
      ref={setNodeRef}
      disableGutters
      secondaryAction={
        <Chip
          label={index + 1}
          size="small"
          sx={{
            borderRadius: 1,
            minWidth: 28,
            bgcolor: "action.selected",
          }}
        />
      }
      sx={{
        px: 0,
        py: 0.5,
        bgcolor: "background.paper",
        opacity: isDragging ? 0.5 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <IconButton
        aria-label={`Drag ${item.title}`}
        size="small"
        sx={{ color: "text.secondary", mr: 3, cursor: "grab" }}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      <Stack sx={{ minWidth: 0 }}>
        <Typography variant="body-medium">{item.title}</Typography>
        <Typography variant="body-medium" color="textSecondary">
          {item.description}
        </Typography>
      </Stack>
    </ListItem>
  );
};

// A successful export either streamed a workbook the browser downloaded, or
// (v2) uploaded it to SharePoint and returned a link to the folder holding it.
type GenerationSuccess =
  | { kind: "download"; fileName: string }
  | { kind: "link"; fileName: string; presentationUrl: string };

// Suppresses a false-positive "File name is required" flash while the
// auto-name fetch is still in flight and the field is transiently empty.
// Only applies while the field still shows the auto-generated name — once the
// analyst types an override, errors reflect what they typed regardless of
// whether the auto-name fetch has resolved.
const shouldShowExportFileNameErrors = ({
  isUsingAutoExportFileName,
  isAutoExportFileNameLoading,
  exportFileNameErrors,
}: {
  isUsingAutoExportFileName: boolean;
  isAutoExportFileNameLoading: boolean;
  exportFileNameErrors: string[];
}): boolean =>
  exportFileNameErrors.length > 0 &&
  (!isUsingAutoExportFileName || !isAutoExportFileNameLoading);

const exportFileNameHintText =
  "Auto-named from Group Name + Effective Date — uses the Revised Effective " +
  "Date if set, otherwise the original Group Effective Date. Click the name " +
  "to override.";

const getExportFileNameHelperText = (
  showExportFileNameErrors: boolean,
  exportFileNameErrors: string[],
): string =>
  showExportFileNameErrors
    ? exportFileNameErrors.join(" ")
    : exportFileNameHintText;

const isGenerateExportDisabled = ({
  rfpId,
  selectedTemplate,
  isWorkbookProcessing,
  isWorkbookRestorePending,
  isGenerating,
  hasExportFileNameErrors,
}: {
  rfpId: string | undefined;
  selectedTemplate: string | null;
  isWorkbookProcessing: boolean;
  isWorkbookRestorePending: boolean;
  isGenerating: boolean;
  hasExportFileNameErrors: boolean;
}): boolean =>
  !rfpId ||
  !selectedTemplate ||
  isWorkbookProcessing ||
  // Until this resolves, an existing append file may not have loaded into
  // workbookFileName/selectedAppendFileBytes yet, so generating now would
  // send excelAppendFile: undefined — which Chameleon treats as "delete it"
  // (CD-1517). Already resolves on every terminal path (found a file, found
  // none, or the check errored), so this doesn't stay stuck on a genuine
  // failure — it just closes the window before that resolution lands.
  isWorkbookRestorePending ||
  isGenerating ||
  hasExportFileNameErrors;

const GenerateExportMpDrawer = ({
  data,
  onClose,
}: DialogComponentProps): React.JSX.Element => {
  const rfpId = data?.rfpId as string | undefined;
  const ecoRfpId = data?.ecoRfpId as string | undefined;
  const { configuration } = useRfpConfiguration(ecoRfpId);
  const { selectedTemplate } = readConfiguration(configuration);

  const [exportFileNameOverride, setExportFileNameOverride] = useState<
    string | null
  >(null);
  const {
    fileName: autoExportFileName,
    isLoading: isAutoExportFileNameLoading,
  } = useAutoExportFileName(rfpId);
  const exportFileName = exportFileNameOverride ?? autoExportFileName;
  const exportFileNameErrors = validateSharePointFileName(exportFileName);
  // Unlike showExportFileNameErrors, this ignores the loading flash: Generate
  // & Export must stay disabled even while the "required" message is hidden,
  // so a still-empty name can't be submitted the instant loading resolves.
  const hasExportFileNameErrors = exportFileNameErrors.length > 0;
  const showExportFileNameErrors = shouldShowExportFileNameErrors({
    isUsingAutoExportFileName: exportFileNameOverride === null,
    isAutoExportFileNameLoading,
    exportFileNameErrors,
  });
  const [generationResult, setGenerationResult] =
    useState<GenerationSuccess | null>(null);
  const [workbookFileName, setWorkbookFileName] = useState<string | null>(null);
  const [isWorkbookProcessing, setIsWorkbookProcessing] = useState(false);
  // Distinguishes "nothing attached," "showing what Chameleon already has,"
  // "the analyst picked a replacement," and "the analyst removed the file" —
  // "removed" must stay distinct from "none" so the fetch-once effect below
  // doesn't repopulate the file Chameleon still has right after it's removed.
  // Seeded from the persisted removal flag so that protection survives a
  // remount (drawer reopen, page refresh), not just the current mount.
  const [workbookSource, setWorkbookSource] = useState<
    "none" | "fetched" | "user-selected" | "removed"
  >(() => (loadIsAppendFileRemoved(rfpId) ? "removed" : "none"));
  const [selectedAppendFileBytes, setSelectedAppendFileBytes] = useState<
    string | null
  >(null);
  const [sheetOrder, setSheetOrder] = useState<SheetOrderItem[]>(() =>
    applySavedSheetOrder(loadSavedSheetOrderIds(rfpId), defaultSheetOrderItems),
  );
  // True until we know whether this RFP already has an attached workbook and,
  // if so, until its sheets have been merged into sheetOrder — the save
  // effect below must not persist the fixed-only order in that window, or it
  // would wipe out a previously saved interleaved arrangement before the
  // merge ever runs. Already resolved (nothing to wait for) when the analyst
  // has persisted-removed the workbook, since the fetch effect never runs in
  // that case.
  const [isWorkbookRestorePending, setIsWorkbookRestorePending] = useState(
    () => Boolean(rfpId) && !loadIsAppendFileRemoved(rfpId),
  );
  const workbookProcessingTimeoutRef = useRef<number | null>(null);
  const workbookFileInputRef = useRef<HTMLInputElement | null>(null);
  // Guards the error toast below to one notification per failed fetch, since
  // the effect it fires from can otherwise re-run on unrelated re-renders.
  const hasNotifiedAppendFileErrorRef = useRef(false);
  // Identifies the "current" workbook parse. appendWorkbookSheetsFromFile is
  // async (it awaits reading the file), so a remove, a replacement, or the
  // fetch-restore racing a manual selection can all resolve out of order — an
  // older parse finishing after a newer decision must not reapply its rows.
  // Bumped by every action that changes what the attached workbook should be;
  // appendWorkbookSheetsFromFile claims its own id and checks it's still
  // current before touching state.
  const workbookRequestIdRef = useRef(0);
  const { createToastNotification } = useToastNotificationService();
  const { data: existingAppendFile, isError: isExistingAppendFileError } =
    useExcelAppendFile(rfpId);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const generateMutation = useGenerateMarketingPresentation();

  const handleDragEnd = ({ active, over }: DragEndEvent): void => {
    if (!over || active.id === over.id) {
      return;
    }

    setSheetOrder((currentSheetOrder) => {
      const activeIndex = currentSheetOrder.findIndex(
        (item) => item.id === active.id,
      );
      const overIndex = currentSheetOrder.findIndex(
        (item) => item.id === over.id,
      );

      if (activeIndex === -1 || overIndex === -1) {
        return currentSheetOrder;
      }

      return arrayMove(currentSheetOrder, activeIndex, overIndex);
    });
  };

  // Persist the full arrangement whenever it changes so a later drawer open
  // (or export) for this RFP restores the analyst's arrangement (CD-379).
  useEffect(() => {
    if (isWorkbookRestorePending) return;
    // A failed existing-append-file check means we don't know whether a
    // previously attached workbook still exists. If a prior save already
    // recorded workbook-sheet ids, this render's workbook-blind (fixed-only)
    // sheetOrder must not overwrite them before the check ever gets a chance
    // to succeed. Limited to the still-untouched "none" state: once the
    // analyst explicitly selects a replacement or removes the workbook,
    // sheetOrder reflects that decision and is authoritative regardless of
    // whether the check ever succeeds — isExistingAppendFileError otherwise
    // stays true for the rest of the mount, which would keep this guard
    // blocking that explicit action's own order from ever being saved.
    const wouldDropKnownWorkbookSheets =
      workbookSource === "none" &&
      isExistingAppendFileError &&
      (loadSavedSheetOrderIds(rfpId) ?? []).some((id) =>
        id.startsWith(workbookSheetIdPrefix),
      );
    if (wouldDropKnownWorkbookSheets) return;
    saveSheetOrderIds(rfpId, sheetOrder);
  }, [
    rfpId,
    sheetOrder,
    isWorkbookRestorePending,
    isExistingAppendFileError,
    workbookSource,
  ]);

  const handleGenerateExport = (): void => {
    if (
      isGenerateExportDisabled({
        rfpId,
        selectedTemplate,
        isWorkbookProcessing,
        isWorkbookRestorePending,
        isGenerating: generateMutation.isPending,
        hasExportFileNameErrors,
      })
    ) {
      return;
    }

    const persistedCarrierOrder = loadAllPagesOrder(rfpId);
    const appliedToAllCarrierOrder =
      persistedCarrierOrder.length > 0 ? persistedCarrierOrder : undefined;

    const trimmedExportName = exportFileName.trim();
    const configuredFileName = trimmedExportName
      ? getExportFileNameWithExtension(trimmedExportName)
      : undefined;

    generateMutation.mutate(
      {
        rfpId,
        templatePath: selectedTemplate,
        sheetOrder: sheetOrder.map((item) => ({
          title: item.title,
          isFromWorkbook: item.source === "workbook",
        })),
        // The carrier order applied to all SBS pages, so the exported sheets
        // match the grids on screen; undefined when none was ever applied.
        carrierOrder: appliedToAllCarrierOrder,
        // Names the workbook stored in SharePoint, so the uploaded file matches
        // the name shown in this drawer rather than a generic default.
        fileName: configuredFileName,
        // Chameleon now treats an omitted/empty append file as "delete it"
        // (CD-1517), so the fetched-but-untouched file must be resent here too.
        // Sent whenever a file name + its bytes are both available (fetched or
        // user-selected); undefined otherwise — including "none"/"removed", or
        // a "user-selected" file whose bytes failed to read.
        excelAppendFile:
          workbookFileName && selectedAppendFileBytes
            ? { fileName: workbookFileName, fileBytes: selectedAppendFileBytes }
            : undefined,
      },
      {
        onSuccess: (result) => {
          if (result.kind === "link") {
            setGenerationResult(result);
            return;
          }
          const exportName = configuredFileName ?? result.fileName;
          downloadBinaryFile(result.blob, exportName);
          setGenerationResult({ kind: "download", fileName: exportName });
        },
      },
    );
  };

  const appendWorkbookSheetsFromFile = useCallback(
    async (file: File): Promise<void> => {
      const requestId = ++workbookRequestIdRef.current;
      try {
        const workbook = read(await file.arrayBuffer(), { type: "array" });
        const visibleSheetNames = getVisibleSheetNames(workbook);
        const workbookSheetItems =
          buildWorkbookSheetOrderItems(visibleSheetNames);
        const savedSheetOrderIds = loadSavedSheetOrderIds(rfpId);

        // A remove or a replacement that happened while this parse was in
        // flight already applied its own outcome — this older parse
        // resolving later must not overwrite it.
        if (workbookRequestIdRef.current !== requestId) return;

        setSheetOrder((currentSheetOrder) =>
          insertWorkbookSheetItems(
            savedSheetOrderIds,
            currentSheetOrder.filter((item) => item.source !== "workbook"),
            workbookSheetItems,
          ),
        );

        const oversizedSheetNames =
          findSheetNamesExceedingExcelLimit(visibleSheetNames);
        if (oversizedSheetNames.length > 0) {
          createToastNotification({
            severity: "warning",
            message: `Sheet name${oversizedSheetNames.length > 1 ? "s" : ""} longer than Excel's 31-character limit will be shortened when exported, so the export's position may not match this preview: ${oversizedSheetNames.join(", ")}.`,
          });
        }
      } catch {
        if (workbookRequestIdRef.current !== requestId) return;

        setWorkbookFileName(null);
        setSheetOrder((currentSheetOrder) =>
          currentSheetOrder.filter((item) => item.source !== "workbook"),
        );
      }
    },
    [rfpId, createToastNotification],
  );

  // Shows whatever excel append file Chameleon already has for this RFP, once,
  // unless the analyst has already touched (fetched-and-applied or replaced)
  // the dropzone. Reuses appendWorkbookSheetsFromFile via a synthesized File so
  // the sheet-order list reflects the already-attached file on open, the same
  // way it would for a freshly uploaded one.
  useEffect(() => {
    if (workbookSource !== "none") return;

    // data stays undefined on a failed fetch too, not just while loading — left
    // unhandled, this effect would return forever on a genuine failure the same
    // way it does while loading, leaving isWorkbookRestorePending stuck and
    // sheet-order persistence disabled for the rest of the session. It's also
    // not safe to treat a failed check as "nothing attached": Generate & Export
    // would then send no append file, and Chameleon treats that as "delete it"
    // (CD-1517) — silently removing a file that's still there but unconfirmed.
    if (isExistingAppendFileError) {
      if (!hasNotifiedAppendFileErrorRef.current) {
        hasNotifiedAppendFileErrorRef.current = true;
        createToastNotification({
          severity: "error",
          message:
            "Couldn't check for a previously attached workbook. If one exists, generating now may remove it.",
        });
      }
      setIsWorkbookRestorePending(false);
      return;
    }

    if (existingAppendFile === undefined) return; // still loading

    const fetchedFile = existingAppendFile.excelAppendFiles[0];
    if (!fetchedFile) {
      setIsWorkbookRestorePending(false);
      return;
    }

    setWorkbookFileName(fetchedFile.fileName);
    setWorkbookSource("fetched");
    setSelectedAppendFileBytes(fetchedFile.fileBytes);
    const restorePromise = appendWorkbookSheetsFromFile(
      new File(
        [base64ToArrayBuffer(fetchedFile.fileBytes)],
        fetchedFile.fileName,
      ),
    );
    // appendWorkbookSheetsFromFile claims its request id synchronously before
    // its first await, so reading the ref right after calling it captures
    // this restore's own id — a later decision (selection, removal) that
    // bumps the ref again must not have this now-stale restore's completion
    // clear the flag out from under it.
    const restoreRequestId = workbookRequestIdRef.current;
    void restorePromise.finally(() => {
      if (workbookRequestIdRef.current === restoreRequestId) {
        setIsWorkbookRestorePending(false);
      }
    });
  }, [
    existingAppendFile,
    isExistingAppendFileError,
    workbookSource,
    appendWorkbookSheetsFromFile,
    createToastNotification,
  ]);

  const queueWorkbookProcessing = (file: File | null): void => {
    if (workbookProcessingTimeoutRef.current) {
      window.clearTimeout(workbookProcessingTimeoutRef.current);
    }
    // Any new decision about the attached workbook invalidates whatever parse
    // (a fetch-restore or an earlier selection) is still in flight, so a slow
    // async result from before this decision can't reapply after it.
    workbookRequestIdRef.current += 1;

    if (!file) {
      setWorkbookFileName(null);
      setIsWorkbookProcessing(false);
      setWorkbookSource("none");
      setSelectedAppendFileBytes(null);
      return;
    }

    setWorkbookFileName(file.name);
    setIsWorkbookProcessing(true);
    setWorkbookSource("user-selected");
    // A fresh selection supersedes any earlier removal — it should surface
    // again on the next open rather than staying suppressed.
    saveIsAppendFileRemoved(rfpId, false);

    // TODO: Replace this mocked queue with the real workbook upload/processing job once the endpoint is available.
    workbookProcessingTimeoutRef.current = window.setTimeout(() => {
      const parsePromise = appendWorkbookSheetsFromFile(file);
      // Captured right after starting the parse, same as the fetch-restore
      // effect: a remove or a replacement selected before this file's bytes
      // finish reading bumps the ref again, and neither the bytes callbacks
      // nor the cleanup below may act on this now-stale selection.
      const requestId = workbookRequestIdRef.current;
      const bytesPromise = file
        .arrayBuffer()
        .then((buffer) => {
          if (workbookRequestIdRef.current !== requestId) return;
          setSelectedAppendFileBytes(arrayBufferToBase64(buffer));
        })
        .catch(() => {
          if (workbookRequestIdRef.current !== requestId) return;
          setSelectedAppendFileBytes(null);
        });

      void Promise.allSettled([parsePromise, bytesPromise]).finally(() => {
        if (workbookRequestIdRef.current !== requestId) return;
        setIsWorkbookProcessing(false);
        // Selecting a file while the existing-append-file fetch is still
        // loading flips workbookSource to "user-selected" before that fetch's
        // effect ever gets a chance to resolve the flag itself — its guard
        // (workbookSource !== "none") then skips it on every later render, so
        // it would otherwise stay pending forever and sheet-order changes
        // would silently stop persisting for the rest of the session.
        setIsWorkbookRestorePending(false);
      });
    }, mockWorkbookProcessingDelayMs);
  };

  useEffect(
    () => () => {
      if (workbookProcessingTimeoutRef.current) {
        window.clearTimeout(workbookProcessingTimeoutRef.current);
      }
    },
    [],
  );

  const handleWorkbookChange = (event: ChangeEvent<HTMLInputElement>): void => {
    queueWorkbookProcessing(getWorkbookFile(event.target.files));
  };

  const handleRemoveWorkbookFile = (): void => {
    if (workbookProcessingTimeoutRef.current) {
      window.clearTimeout(workbookProcessingTimeoutRef.current);
    }
    // Invalidates a workbook parse still in flight (e.g. the fetch-restore of
    // an already-attached file) so its result can't reapply the rows this
    // removal is about to clear.
    workbookRequestIdRef.current += 1;

    // Reset the underlying <input>'s value too — most browsers won't fire
    // onChange for re-selecting the same file otherwise.
    if (workbookFileInputRef.current) {
      workbookFileInputRef.current.value = "";
    }

    setWorkbookFileName(null);
    setIsWorkbookProcessing(false);
    setWorkbookSource("removed");
    setSelectedAppendFileBytes(null);
    // Removal is a definitive, terminal decision — it makes any in-flight
    // restore moot, so resolve the pending flag here rather than leaving it
    // stuck (now that the restore's own .finally() correctly no-ops for a
    // stale request id, nothing else would clear it if removal raced it).
    setIsWorkbookRestorePending(false);
    setSheetOrder((currentSheetOrder) =>
      currentSheetOrder.filter((item) => item.source !== "workbook"),
    );
    // Chameleon only deletes the SharePoint append file as a side effect of
    // Generate & Export, so this alone doesn't reach the backend — persist
    // the removal so reopening the drawer (or refreshing) doesn't re-fetch
    // and redisplay the file Chameleon still has until that next export.
    saveIsAppendFileRemoved(rfpId, true);
  };

  const handleWorkbookDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    queueWorkbookProcessing(getWorkbookFile(event.dataTransfer.files));
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, minHeight: 64, borderBottom: 1, borderColor: "divider" }}
      >
        <Typography variant="body-large" color="textSecondary">
          Generate & Export MP
        </Typography>
        <IconButton
          aria-label="Close generate and export MP drawer"
          onClick={() => onClose()}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Stack sx={{ flex: 1, overflow: "auto", px: 2, py: 2 }} spacing={3}>
        {generateMutation.isError ? (
          <Alert severity="error">
            Failed to generate marketing presentation. Please try again.
          </Alert>
        ) : null}

        {generationResult ? (
          <Alert
            severity="success"
            title="MP Generated Successfully"
            sx={{ width: "100%" }}
          >
            <Typography variant="body-medium" component="p" sx={{ mb: 2 }}>
              <em>{generationResult.fileName}</em>
            </Typography>
            {generationResult.kind === "link" ? (
              <Link
                href={generationResult.presentationUrl}
                target="_blank"
                rel="noopener noreferrer"
                underline="always"
                sx={{ color: paletteVar("secondary-main", "#0078a2") }}
              >
                Click here to open the Marketing folder in SharePoint
              </Link>
            ) : null}
          </Alert>
        ) : (
          <>
            <Stack spacing={1}>
              <FormLabel
                htmlFor="generate-export-mp-file-name"
                sx={formLabelSx}
              >
                Export File Name
              </FormLabel>
              <TextField
                id="generate-export-mp-file-name"
                value={exportFileName}
                onChange={(event) =>
                  setExportFileNameOverride(
                    stripXlsxExtension(event.target.value),
                  )
                }
                fullWidth
                size="sm"
                error={showExportFileNameErrors}
                helperText={getExportFileNameHelperText(
                  showExportFileNameErrors,
                  exportFileNameErrors,
                )}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">.xlsx</InputAdornment>
                    ),
                  },
                }}
              />
            </Stack>

            <Stack
              spacing={1}
              sx={{
                border: 1,
                borderColor: "rgba(0, 0, 0, 0.23)",
                borderRadius: 1,
                p: 2,
              }}
            >
              <FormLabel sx={formLabelSx}>Upload Workbook</FormLabel>
              <Typography variant="caption">
                Attach Files to be included in Marketing Presentation
              </Typography>
              <Box
                role="group"
                aria-label="Upload workbook dropzone"
                onDragOver={(event) => event.preventDefault()}
                onDrop={handleWorkbookDrop}
                sx={{
                  border: 1,
                  borderStyle: "dashed",
                  borderColor: "divider",
                  py: 3,
                  px: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <UploadFileOutlinedIcon
                  fontSize="small"
                  sx={{ color: "action.active" }}
                />
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ textAlign: "center" }}
                >
                  Drag and drop file here or{" "}
                  <Button
                    component="label"
                    variant="tertiary"
                    size="sm"
                    sx={{
                      minWidth: 0,
                      p: 0,
                      fontSize: "inherit",
                      lineHeight: "inherit",
                      textTransform: "none",
                      verticalAlign: "baseline",
                    }}
                  >
                    Choose File
                    <input
                      ref={workbookFileInputRef}
                      hidden
                      type="file"
                      accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      onChange={handleWorkbookChange}
                    />
                  </Button>
                </Typography>
                {workbookFileName ? (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="caption" color="textSecondary">
                      {workbookFileName}
                    </Typography>
                    <IconButton
                      aria-label={`Remove ${workbookFileName}`}
                      size="small"
                      onClick={handleRemoveWorkbookFile}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  </Stack>
                ) : null}
                {isWorkbookProcessing ? (
                  <Typography variant="caption" color="textSecondary">
                    Processing workbook...
                  </Typography>
                ) : null}
                <Typography variant="caption" color="textSecondary">
                  Supported Files: .xlsx
                </Typography>
              </Box>
            </Stack>

            <Stack spacing={1}>
              <Typography variant="body-large">
                <strong>Sheet Order</strong>
              </Typography>
              <Typography variant="caption">
                Rearrange the Order of the pages in the Marketing Presentation
              </Typography>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={sheetOrder.map((item) => item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <List disablePadding sx={{ py: 1 }}>
                    {sheetOrder.map((item, index) => (
                      <SortableSheetOrderItem
                        key={item.id}
                        item={item}
                        index={index}
                      />
                    ))}
                  </List>
                </SortableContext>
              </DndContext>
            </Stack>
          </>
        )}
      </Stack>

      <Divider />
      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={2}
        sx={{ px: 2, py: 1.5 }}
      >
        <Button variant="tertiary" onClick={() => onClose()}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleGenerateExport}
          disabled={isGenerateExportDisabled({
            rfpId,
            selectedTemplate,
            isWorkbookProcessing,
            isWorkbookRestorePending,
            isGenerating: generateMutation.isPending,
            hasExportFileNameErrors,
          })}
          startIcon={
            generateMutation.isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {generateMutation.isPending ? "Generating…" : "Generate & Export"}
        </Button>
      </Stack>
    </Box>
  );
};

export default GenerateExportMpDrawer;
